/**
 * A sorted array data structure optimized for the advancing front algorithm.
 * Maintains elements in sorted order by a priority value (angle) and provides
 * efficient operations for insertion, removal, and updates.
 *
 * @template T The type of elements stored in the array
 */
export default class SortedArray<T> {
  private items: T[] = [];
  private keyFunction: (item: T) => number;
  private itemToIndex = new Map<T, number>();

  /**
   * Creates a new SortedArray instance.
   * @param keyFunction Function that extracts the sort key (priority) from each item
   */
  constructor(keyFunction: (item: T) => number) {
    this.keyFunction = keyFunction;
  }

  /**
   * Returns the number of items in the array.
   */
  public length(): number {
    return this.items.length;
  }

  /**
   * Returns true if the array is empty.
   */
  public isEmpty(): boolean {
    return this.items.length === 0;
  }

  /**
   * Inserts an item into the array maintaining sorted order.
   * Uses binary search to find the correct insertion position.
   * @param item The item to insert
   */
  public insert(item: T): void {
    const key = this.keyFunction(item);
    const insertIndex = this.binarySearchInsertPosition(key);

    // Insert the item at the correct position
    this.items.splice(insertIndex, 0, item);

    // Update the index map for all affected items
    this.updateIndexMap(insertIndex);
  }

  /**
   * Removes and returns the item with the smallest key (priority).
   * @returns The item with the smallest key, or null if array is empty
   */
  public popSmallest(): T | null {
    if (this.items.length === 0) {
      return null;
    }

    const item = this.items.shift()!;
    this.itemToIndex.delete(item);

    // Update index map for remaining items
    this.updateIndexMap(0);

    return item;
  }

  /**
   * Removes a specific item from the array.
   * @param item The item to remove
   * @returns True if the item was found and removed, false otherwise
   */
  public remove(item: T): boolean {
    const index = this.itemToIndex.get(item);
    if (index === undefined) {
      return false;
    }

    this.items.splice(index, 1);
    this.itemToIndex.delete(item);

    // Update index map for affected items
    this.updateIndexMap(index);

    return true;
  }

  /**
   * Updates an existing item in the array. This removes the old item
   * and inserts the updated item in the correct sorted position.
   * @param oldItem The item to update
   * @param newItem The new item to replace it with
   * @returns True if the item was found and updated, false otherwise
   */
  public update(oldItem: T, newItem: T): boolean {
    if (!this.remove(oldItem)) {
      return false;
    }

    this.insert(newItem);
    return true;
  }

  /**
   * Checks if a specific item exists in the array.
   * @param item The item to check for
   * @returns True if the item exists, false otherwise
   */
  public has(item: T): boolean {
    return this.itemToIndex.has(item);
  }

  /**
   * Returns all items in the array (in sorted order).
   * @returns A copy of the internal array
   */
  public getAll(): T[] {
    return [...this.items];
  }

  /**
   * Clears all items from the array.
   */
  public clear(): void {
    this.items.length = 0;
    this.itemToIndex.clear();
  }

  /**
   * Returns the item at the specified index (0-based).
   * @param index The index to retrieve
   * @returns The item at the index, or undefined if index is out of bounds
   */
  public at(index: number): T | undefined {
    return this.items[index];
  }

  /**
   * Returns the item with the smallest key without removing it.
   * @returns The item with the smallest key, or null if array is empty
   */
  public peek(): T | null {
    return this.items.length > 0 ? this.items[0] : null;
  }

  /**
   * Uses binary search to find the correct insertion position for a given key.
   * @param key The key to find insertion position for
   * @returns The index where the item should be inserted
   */
  private binarySearchInsertPosition(key: number): number {
    let left = 0;
    let right = this.items.length;

    while (left < right) {
      const mid = Math.floor((left + right) / 2);
      const midKey = this.keyFunction(this.items[mid]);

      if (midKey < key) {
        left = mid + 1;
      } else {
        right = mid;
      }
    }

    return left;
  }

  /**
   * Updates the index mapping for items starting from the given index.
   * This is called after insertions or removals to keep the mapping accurate.
   * @param fromIndex The index to start updating from
   */
  private updateIndexMap(fromIndex: number): void {
    for (let i = fromIndex; i < this.items.length; i++) {
      this.itemToIndex.set(this.items[i], i);
    }
  }

  /**
   * Debug method to verify the array is properly sorted.
   * @returns True if the array is sorted, false otherwise
   */
  public isSorted(): boolean {
    for (let i = 1; i < this.items.length; i++) {
      const prevKey = this.keyFunction(this.items[i - 1]);
      const currKey = this.keyFunction(this.items[i]);
      if (prevKey > currKey) {
        return false;
      }
    }
    return true;
  }

  /**
   * Debug method to verify the index mapping is correct.
   * @returns True if the mapping is correct, false otherwise
   */
  public hasValidMapping(): boolean {
    if (this.itemToIndex.size !== this.items.length) {
      return false;
    }

    for (let i = 0; i < this.items.length; i++) {
      const mappedIndex = this.itemToIndex.get(this.items[i]);
      if (mappedIndex !== i) {
        return false;
      }
    }

    return true;
  }
}
